import React, { useState } from 'react';
import { LabelWithDescription } from '@/components/LabelWithdescription';
import {
	CaretRightOutlined,
	InfoCircleOutlined,
	LoadingOutlined,
	MinusCircleOutlined,
	PlusOutlined,
} from '@ant-design/icons';
import { DeleteOutline } from '@mui/icons-material';
import {
	Button,
	Card,
	Flex,
	Form,
	Input,
	notification,
	Spin,
	Radio,
	Collapse,
	Tooltip,
} from 'antd';
import { OverViewCardForm } from '../OverviewCardForm';
import { QueryObserverResult, RefetchOptions, UseMutationResult } from '@tanstack/react-query';
import { useAppDispatch } from '@/store/hooks/useAppDispatch';
import { closeModal } from '@/store/modal/slice';
import './CallTransferingWorkflowForm.scss';
import { CallWorkflow, User } from '@/types/TAccounts';
import PhoneNumberInput from '@/components/Inputs/PhoneNumberInput';
import { handlePrefixPlusOnPhone } from '@/utils/helper';
import ShortUniqueId from 'short-unique-id';
import { analyzeAndUpdateWorkflow } from '@/utils/analysisWorkflow';
import { WorkflowType } from 'src/enums/EWorkflow';
import { getSelectedAgentId } from '@/store/account/selector';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { selectCanAddScenario } from '@/store/scenarioCounts/selector';
import { incrementScenarioCount } from '@/store/scenarioCounts/slice';
import ConfirmDeleteModal from '@/components/ConfirmDeleteModal/ConfirmDeleteModal';
import { ECallTransferType } from 'src/enums/ECallTransfer';

const uid = new ShortUniqueId({ length: 10 });

interface CallTransferingWorkflowFormProps {
	callTransferingWorkflowData: CallWorkflow[];
	showAddFieldButton?: boolean;
	refetch: (options?: RefetchOptions) => Promise<QueryObserverResult<User, Error>>;
	mutation: UseMutationResult<unknown, Error, any, unknown>;
	scenarioIndex?: number;
	initialValues?: CallWorkflow[];
	isDuplicate?: boolean;
}

const CallTransferingWorkflowFormAdd: React.FC<CallTransferingWorkflowFormProps> = ({
	callTransferingWorkflowData = [],
	showAddFieldButton = true,
	refetch,
	mutation,
	scenarioIndex,
	initialValues,
	isDuplicate = false,
}) => {
	const [form] = Form.useForm();
	const dispatch = useAppDispatch();

	const [isFieldTouchReset, setIsFieldTouchReset] = useState<boolean>(true);
	const [activeKeys, setActiveKeys] = useState<string | string[]>();
	const selectedAgentId = useAppSelector(getSelectedAgentId);
	const canAddScenario = useAppSelector(selectCanAddScenario);

	const handleAddScenario = (add: () => void) => {
		if (!canAddScenario) {
			notification.error({
				message: 'Scenario limit reached',
				description: 'Max scenarios (100) reached. Please Delete existing to add more.',
			});
			return;
		}
		add();
	};

	const handleExtensionChange = (value: string, fieldName: number) => {
		// If extension has value, automatically set to Hot transfer
		if (value && value.trim() !== '') {
			form.setFieldValue(['callWorkflows', fieldName, 'whisperType'], ECallTransferType.HOT);
		}
	};

	const handleSubmit = async (values: { callWorkflows: CallWorkflow[] }) => {
		try {
			const newWf = values.callWorkflows
				? values.callWorkflows.map((item) => ({ ...item, id: uid.rnd() }))
				: [];
			const callWf = callTransferingWorkflowData
				? newWf.concat(callTransferingWorkflowData)
				: newWf;

			const callWfString = callWf.map((item) => {
				const filteredNums = item?.backupNumbers?.filter((num) => num?.length > 7);
				return {
					...item,
					workflowForwardingNumber: handlePrefixPlusOnPhone(item.workflowForwardingNumber),
					backupNumbers: filteredNums?.map((num) => handlePrefixPlusOnPhone(num)) || [],
				};
			});

			await mutation.mutateAsync({
				callWorkflows: callWfString,
				agentId: selectedAgentId as string,
			});

			dispatch(incrementScenarioCount(WorkflowType.CALL));

			refetch();
			notification.success({
				key: 'form-success',
				message: isDuplicate
					? 'Call Transfering workflow has been duplicated successfully!'
					: 'Call Transfering workflow has been created successfully!',
				duration: 3,
			});
			setIsFieldTouchReset(false);
			dispatch(closeModal());
			await analyzeAndUpdateWorkflow(
				{ workflows: callWfString },
				WorkflowType.CALL,
				dispatch,
				selectedAgentId as string,
			);
		} catch (err) {
			console.log(err);
		}
	};

	const handleConfirmDelete = (remove: (name: number) => void, name: number) => {
		ConfirmDeleteModal({
			itemName: 'Scenario',
			onConfirm: () => remove(name),
		});
	};

	const FormItems = () => (
		<Form.List name="callWorkflows" initialValue={initialValues || [{}]}>
			{(fields, { add, remove }) => (
				<div className="card-container mr-4">
					{fields.map(({ key, name, ...restField }, index) => (
						<div key={key} className="mb-4 border rounded-lg shadow-lg">
							<Card
								className="flex-1"
								title={
									!isDuplicate ? (
										<div className="flex justify-between">
											<span className="font-bold text-base">
												Scenario {(scenarioIndex ?? 0) + index + 1}
											</span>
											{fields.length > 1 && showAddFieldButton && (
												<Button
													type="primary"
													danger
													icon={<DeleteOutline />}
													onClick={() => handleConfirmDelete(remove, name)}
												>
													Delete
												</Button>
											)}
										</div>
									) : (
										<span className="font-bold text-base">Customize Your Duplicate</span>
									)
								}
							>
								<LabelWithDescription
									customClassName="mt-2"
									label="Scenario Description"
									description="Describe the scenario in which you would like to transfer the call."
								/>
								<Form.Item
									className=" mt-2 pt-2 "
									{...restField}
									name={[name, 'description']}
									rules={[
										{
											validator: (_, value) => {
												if (!value || value.trim() === '') {
													return Promise.reject("Description can't be blank");
												}
												return Promise.resolve();
											},
										},
									]}
								>
									<Input placeholder="e.g. Transfer the caller to the billing department. Execute whenever caller asks for a refund or receipt." />
								</Form.Item>

								<div>
									<LabelWithDescription
										customClassName="mt-2"
										label="Phone Number"
										description="Specify the number you want your AI receptionist to forward the call to when the scenario is triggered."
									/>
									<div className="input-container mt-2">
										<Flex gap={8}>
											<Form.Item className="flex-auto !mb-2" label="Number:">
												<PhoneNumberInput name={[name, 'workflowForwardingNumber']} />
											</Form.Item>
											<div className="mt-10">#</div>
											<Form.Item
												name={[name, 'workflowForwardingDID']}
												className="flex-auto !mb-2"
												label={
													<Tooltip title="Send an extension digit when transferring the call (recommended for IVR systems)">
														<div className="flex items-center gap-1">
															Extension (Optional):
															<InfoCircleOutlined
																style={{ fontSize: '12px' }}
																className="text-gray-400 cursor-help"
															/>
														</div>
													</Tooltip>
												}
											>
												<Input
													placeholder="e.g: 302"
													onKeyPress={(event) => {
														const keyCode = event.keyCode || event.which;
														const keyValue = String.fromCharCode(keyCode);
														if (!/^\d+$/.test(keyValue)) {
															event.preventDefault();
														}
													}}
													onChange={(e) => handleExtensionChange(e.target.value, name)}
												/>
											</Form.Item>
										</Flex>
									</div>
								</div>

								<LabelWithDescription
									customClassName="mt-2 mb-2"
									label="Voice Response"
									description='Specify what you want your AI receptionist to say right before the call is transferred. Defaults to "Please hold while I transfer your call."'
								/>
								<Form.Item className=" mt-2 pt-2 " {...restField} name={[name, 'voiceResponse']}>
									<Input
										placeholder="e.g. Sounds good, I am transferring you to the billing department right now. Please hold."
										defaultValue={'Please hold while I transfer your call.'}
									/>
								</Form.Item>

								<Collapse
									className="mt-3"
									bordered={false}
									expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
									activeKey={activeKeys}
									onChange={(key) => setActiveKeys(key)}
									items={[
										{
											key: '1',
											label: <p className="font-medium my-0">Advanced Settings</p>,
											children: (
												<>
													<div>
														<Form.Item noStyle shouldUpdate>
															{({ getFieldValue }) => {
																const workflowForwardingDID = getFieldValue([
																	'callWorkflows',
																	name,
																	'workflowForwardingDID',
																]);

																return (
																	<Form.Item
																		label="Call Transfer Type"
																		{...restField}
																		name={[name, 'whisperType']}
																		dependencies={[
																			['callWorkflows', name, 'workflowForwardingDID'],
																		]}
																	>
																		<Radio.Group
																			name="whisperType"
																			{...restField}
																			defaultValue={ECallTransferType.COLD}
																			value={
																				form.getFieldValue([
																					'callWorkflows',
																					name,
																					'whisperType',
																				]) || ECallTransferType.COLD
																			}
																			options={[
																				{
																					label: (
																						<Tooltip
																							title={
																								<div className="flex flex-col">
																									<span>• Normal Call Transfer</span>
																									<span>
																										• The call is transferred using the caller's
																										number
																									</span>
																								</div>
																							}
																						>
																							<div className="flex items-center gap-1">
																								Cold Transfer
																								<InfoCircleOutlined
																									style={{ fontSize: '12px' }}
																									className="text-gray-400 cursor-help"
																								/>
																							</div>
																						</Tooltip>
																					),
																					value: ECallTransferType.COLD,
																					disabled:
																						workflowForwardingDID && workflowForwardingDID !== '',
																				},
																				{
																					label: (
																						<Tooltip
																							title={
																								<div className="flex flex-col">
																									<span>• Advanced Call Transfer</span>
																									<span>
																										• The call is transferred using the AI number
																									</span>
																									<span>• Extension digit support</span>
																									<span>
																										• Call whisper handoff message support
																									</span>
																									<span>• Backup number dialing available</span>
																								</div>
																							}
																						>
																							<div className="flex items-center gap-1">
																								Hot Transfer
																								<InfoCircleOutlined
																									style={{ fontSize: '12px' }}
																									className="text-gray-400 cursor-help"
																								/>
																							</div>
																						</Tooltip>
																					),
																					value: ECallTransferType.HOT,
																				},
																			]}
																		/>
																	</Form.Item>
																);
															}}
														</Form.Item>

														<Form.Item noStyle shouldUpdate>
															{({ getFieldValue }) => {
																const whisperType = getFieldValue([
																	'callWorkflows',
																	name,
																	'whisperType',
																]);
																const workflowForwardingDID = getFieldValue([
																	'callWorkflows',
																	name,
																	'workflowForwardingDID',
																]);
																return (
																	whisperType === ECallTransferType.HOT && (
																		<>
																			{/* Handoff Message Part */}
																			{!workflowForwardingDID && (
																				<>
																					<Form.Item
																						label="Handoff Message"
																						{...restField}
																						name={[name, 'handoffMessageType']}
																					>
																						<Radio.Group
																							name="handoffMessageType"
																							{...restField}
																							defaultValue={'Prompt'}
																							options={[
																								{
																									label: 'Auto-generate Whisper Handoff Message',
																									value: 'Prompt',
																								},
																								{
																									label: 'Static Whisper Handoff Message',
																									value: 'Static Word',
																								},
																							]}
																						/>
																					</Form.Item>
																					<Form.Item noStyle shouldUpdate>
																						{({ getFieldValue }) => {
																							const handoffMessageType = getFieldValue([
																								'callWorkflows',
																								name,
																								'handoffMessageType',
																							]);
																							return (
																								handoffMessageType === 'Static Word' && (
																									<Form.Item
																										{...restField}
																										name={[name, 'handoffMessage']}
																										rules={[
																											{
																												required: true,
																												message: "Handsoff Message can't be blank",
																											},
																										]}
																									>
																										<Input placeholder="e.g. Summarize the above conversation" />
																									</Form.Item>
																								)
																							);
																						}}
																					</Form.Item>
																				</>
																			)}

																			{/* Backup Numbers Part */}
																			<div className="mt-4">
																				<Tooltip title="If the main phone number is unavailable, add backup numbers to attempt calling in sequence.">
																					<span className="text-gray-400">
																						Backup Numbers (optional)
																						<InfoCircleOutlined
																							style={{ fontSize: '12px' }}
																							className="text-gray-400 cursor-help ml-2"
																						/>
																					</span>
																				</Tooltip>
																				<Form.List
																					name={[name, 'backupNumbers']}
																					initialValue={['']}
																				>
																					{(
																						backupFields,
																						{ add: addBackupNumber, remove: removeBackupNumber },
																					) => (
																						<>
																							{backupFields.map(
																								({ key: backupKey, name: backupName }) => (
																									<div
																										key={backupKey}
																										className="flex items-center gap-2 w-full"
																									>
																										<Form.Item
																											className="pt-0 w-full"
																											name={[backupName]}
																											label="Phone Number"
																										>
																											<PhoneNumberInput
																												name={backupName}
																												required={false}
																											/>
																										</Form.Item>

																										{backupFields.length > 1 && (
																											<MinusCircleOutlined
																												onClick={() =>
																													removeBackupNumber(backupName)
																												}
																												className="text-red-600 text-lg pt-0 cursor-pointer"
																											/>
																										)}
																									</div>
																								),
																							)}
																							<Form.Item>
																								<Button
																									type="dashed"
																									onClick={() => addBackupNumber('')}
																									block
																									icon={<PlusOutlined />}
																								>
																									Add Number
																								</Button>
																							</Form.Item>
																						</>
																					)}
																				</Form.List>
																			</div>
																		</>
																	)
																);
															}}
														</Form.Item>
													</div>
												</>
											),
										},
									]}
								/>
							</Card>
						</div>
					))}
					{showAddFieldButton && (
						<Form.Item>
							<Button
								type="dashed"
								onClick={() => handleAddScenario(add)}
								block
								icon={<PlusOutlined />}
							>
								Add Call Transfering Workflow
							</Button>
						</Form.Item>
					)}
				</div>
			)}
		</Form.List>
	);

	return (
		<>
			<Spin
				spinning={mutation.isPending}
				indicator={<LoadingOutlined style={{ fontSize: '48px' }} spin />}
			>
				<div className="max-h-[60vh] overflow-auto">
					<OverViewCardForm
						form={form}
						initialValues={undefined}
						formItems={<FormItems />}
						handleSubmit={handleSubmit}
						isFieldTouchReset={isFieldTouchReset}
						hideUnsavedChangesModal={isDuplicate}
					/>
				</div>
			</Spin>
		</>
	);
};

export default CallTransferingWorkflowFormAdd;
